import { MARKED_OPTIONS, MarkedOptions } from "ngx-markdown";
import { Hooks, Renderer } from "marked";
import { preserveMarkdownLineBreaks } from "./app/shared/utils/markdown-line-breaks.util";

/**
 * Normalizes a URL by removing protocol, www prefix, and trailing slashes
 * for comparison purposes to detect autolinks.
 */
function normalizeUrl(url: string): string {
  return url
    .replace(/^https?:\/\//, '')  // Remove http:// or https://
    .replace(/^www\./, '')         // Remove www. prefix
    .replace(/\/$/, '')            // Remove trailing slash
    .toLowerCase();
}

// Custom renderer factory to disable autolinking of URLs
// Only explicit markdown links [text](url) will be rendered as links
export function markedOptionsFactory(): MarkedOptions {
  const renderer = new Renderer();
  const hooks = new Hooks();
  hooks.preprocess = preserveMarkdownLineBreaks;

  // WalkTokens function to convert autolinks to text tokens
  const walkTokens = (token: any) => {
    if (token.type === 'link') {
      // Check if this is an autolink (tokens empty or text equals href)
      const hasTokens = token.tokens && Array.isArray(token.tokens) && token.tokens.length > 0;

      if (!hasTokens) {
        // No tokens means it's an autolink - convert to text token
        token.type = 'text';
        token.text = token.href;
        token.raw = token.href;
        delete token.href;
        delete token.title;
        delete token.tokens;
      } else {
        // Has tokens - check if text equals href (using normalized comparison)
        const extractText = (t: any): string => {
          if (typeof t === 'string') return t;
          if (t.type === 'text' && t.text) return t.text;
          if (t.raw) return t.raw;
          if (t.literal) return t.literal;
          if (t.tokens && Array.isArray(t.tokens)) {
            return t.tokens.map(extractText).join('');
          }
          return '';
        };

        const text = token.tokens.map(extractText).join('').trim();

        // Normalize both text and href for comparison
        // This handles cases like www.google.com (text) vs http://www.google.com (href)
        const normalizedText = normalizeUrl(text);
        const normalizedHref = normalizeUrl(token.href);

        // Also check original values for exact matches
        const isExactMatch = !text || text === token.href || text === decodeURIComponent(token.href);
        const isNormalizedMatch = normalizedText === normalizedHref && normalizedText.length > 0;

        if (isExactMatch || isNormalizedMatch) {
          // Text equals href (normalized) - it's an autolink, convert to text
          token.type = 'text';
          token.text = token.href;
          token.raw = token.href;
          delete token.href;
          delete token.title;
          delete token.tokens;
        }
      }
    }
  };

  return {
    renderer: renderer,
    gfm: true,
    breaks: true,
    hooks,
    walkTokens: walkTokens,
  };
}

export const markdownConfig = {
  markedOptions: {
    provide: MARKED_OPTIONS,
    useFactory: markedOptionsFactory,
  },
};
